/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store/bootstrap';
import { filter as slackChannelFilter } from '../../../../../controllers/store/search/filters/slack-channel';

import { i18n } from './messages';

/**
 * Renders the "#" channel sigil as a CSS pseudo-element so it is purely decorative
 * and does not appear in the option's text label (which is used for filtering/search).
 */
const channelLabelStyles = css({
	'&::before': {
		content: '"#"',
		color: token('color.text.subtlest'),
		marginRight: token('space.100'),
	},
});

const SlackChannelFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();

	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: slackChannelFilter.id,
	});

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const label = fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.label);

	const groupedOptions = [
		{
			label: formatMessage(i18n.selectedLabel),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.suggestedLabel),
			options: suggestedOptions,
		},
	];

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option) => {
				setOption(option);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.placeholder)}
			filterOrControlId="slackChannel"
			testId="search-dialog-slack-channel-filter"
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: slackChannelFilter.id });
			}}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			hideOnHoverOut={hideOnHoverOut}
			formatOptionLabel={(option) => <span css={channelLabelStyles}>{option.label}</span>}
		/>
	);
};

export default SlackChannelFilter;
